// FILE: Upgrade.js
import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Check, CreditCard, Calendar, Clock, Shield, Settings } from "lucide-react";
import { Button } from "../../ui/button";
import pricingPlansUpgrade from "./hooks/pricingPlansUpgrade";
import {
    Box,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button as MuiButton
} from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    ErrorOutline as ErrorOutlineIcon,
    Warning as WarningIcon,
    Info as InfoIcon
} from "@mui/icons-material";
import api from "../../components/axios/axiosInstance";

const PRICE_MONTHLY_RECURRING = process.env.REACT_APP_PRICE_MONTHLY_ID;
const PRICE_ANNUAL_RECURRING = process.env.REACT_APP_PRICE_ANNUAL_ID;
const PRICE_TEST = process.env.REACT_APP_PRICE_TEST_ID;

const getCorrectPriceId = (planName) => {
    if (planName === "Monthly") return PRICE_MONTHLY_RECURRING;
    if (planName === "Annual") return PRICE_ANNUAL_RECURRING;
    if (planName === "Enterprise") return PRICE_TEST;
    return null;
};

const Upgrade = () => {
    const [planInfo, setPlanInfo] = useState(null);
    const [loadingPlan, setLoadingPlan] = useState(true);
    const [error, setError] = useState(null);
    const [successDialog, setSuccessDialog] = useState({ open: false, message: "", title: "" });
    const [errorDialog, setErrorDialog] = useState({ open: false, message: "", title: "" });
    const [infoDialog, setInfoDialog] = useState({ open: false, message: "", title: "" });
    const visiblePlanIndexes = [1, 2];

    const fetchPlanInfo = useCallback(async () => {
        try {
            const res = await api.get(`/auth/teacher/plan-status/`);
            setPlanInfo(res.data);

        } catch (err) {
            console.error("❌ Failed to fetch plan info:", err);
            if (err.response?.status === 401) {
                setError("Authentication required. Please log in again.");
                // Redirect to login if unauthorized
                setTimeout(() => {
                    window.location.href = "/teacher-login";
                }, 2000);
            } else {
                setError("Could not load subscription status.");
            }
        } finally {
            setLoadingPlan(false);
        }
    }, []);

    useEffect(() => {
        fetchPlanInfo();

        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("payment") === "success") {
            console.log("✅ Payment succeeded. Refreshing plan info...");
            setSuccessDialog({
                open: true,
                title: "Subscription Activated!",
                message: "Your subscription has been successfully activated. Welcome to Premium!"
            });
            fetchPlanInfo();
            // Clear the URL parameter after showing the success message
            setTimeout(() => {
                window.history.replaceState({}, document.title, window.location.pathname);
            }, 500);
        }

        if (urlParams.get("updated") === "success") {
            console.log("✅ Subscription updated via billing portal...");
            setSuccessDialog({
                open: true,
                title: "Subscription Updated!",
                message: "Your subscription settings have been successfully updated."
            });
            fetchPlanInfo();
            // Clear the URL parameter after showing the success message
            setTimeout(() => {
                window.history.replaceState({}, document.title, window.location.pathname);
            }, 500);
        }
    }, []);

    const handleSubscribe = async (priceId) => {
        if (!priceId) return;
        try {
            const res = await api.post(
                `/grades/create-checkout-session/`,
                { price_id: priceId },
                { headers: { 'Content-Type': 'application/json' } }
            );
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                console.warn("Checkout API returned:", res.data);
                setInfoDialog({
                    open: true,
                    title: "Active Subscription Detected",
                    message: "You already have an active subscription! Please click 'Manage Subscription' below to cancel or upgrade your current subscription."
                });
            }
        } catch (err) {
            console.error("Checkout error:", err?.response?.status, err?.response?.data);
            setInfoDialog({
                open: true,
                title: "Subscription Management",
                message: "You already have an active subscription! Please use the 'Manage Subscription' button below to upgrade, downgrade, or modify your current subscription plan."
            });
        }
    };

    const handleOpenBillingPortal = async () => {
        try {
            const res = await api.post(
                `/grades/create-billing-portal-session/`,
                {}
            );
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                setErrorDialog({
                    open: true,
                    title: "Portal Access Failed",
                    message: "Unable to open the billing portal. Please try again in a few moments."
                });
            }
        } catch (err) {
            console.error("Billing portal error:", err);
            setErrorDialog({
                open: true,
                title: "Error Occurred",
                message: "Something went wrong while accessing your billing portal. Please try again."
            });
        }
    };

    const renderPlanLabel = () => {
        if (!planInfo) return "Free Plan";
        if (planInfo.plan_type === "free") return "Free Plan";
        if (planInfo.plan_type === "monthly") return "Premium Monthly";
        if (planInfo.plan_type === "annual") return "Premium Annual";
        if (planInfo.plan_type === "test") return "Test Plan";
        return planInfo.plan_type;
    };

    const getPlanStatusColor = () => {
        if (!planInfo) return "text-gray-600";
        if (planInfo.is_expired) return "text-red-600";
        if (planInfo.plan_type === "free") return "text-gray-600";
        return "text-green-600";
    };

    const getPlanStatusBg = () => {
        if (!planInfo) return "bg-gray-100";
        if (planInfo.is_expired) return "bg-red-50 border-red-200";
        if (planInfo.plan_type === "free") return "bg-gray-50 border-gray-200";
        return "bg-green-50 border-green-200";
    };

    return (
        <div className="min-h-screen bg-cream">
            {/* Header */}
            <div className="bg-white border-b border-secondary/30 py-8">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center"
                    >
                        <h1 className="text-4xl font-bold text-primary mb-3">
                            Subscription Management
                        </h1>
                        <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
                            Manage your subscription, upgrade your plan, or explore premium features to enhance your teaching experience.
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Left Sidebar - Subscription Details */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="lg:col-span-1"
                    >
                        <div className="bg-white rounded-2xl shadow-lg border border-secondary/20 p-6 sticky top-8">
                            <div className="text-center mb-6">
                                <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/80 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <CreditCard className="w-8 h-8 text-white" />
                                </div>
                                <h2 className="text-xl font-bold text-primary mb-2">Current Plan</h2>
                            </div>

                            {loadingPlan ? (
                                <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" py={4}>
                                    <CircularProgress size={32} className="text-primary mb-3" />
                                    <Typography className="text-sm text-foreground/60">Loading subscription...</Typography>
                                </Box>
                            ) : error ? (
                                <div className="text-center py-6">
                                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Shield className="w-6 h-6 text-red-500" />
                                    </div>
                                    <Typography className="text-red-600 font-medium text-sm">{error}</Typography>
                                </div>
                            ) : (
                                <>
                                    {/* Plan Status Card */}
                                    <div className={`rounded-xl p-4 border-2 mb-6 ${getPlanStatusBg()}`}>
                                        <div className="text-center">
                                            <h3 className={`text-lg font-bold ${getPlanStatusColor()} mb-1`}>
                                                {renderPlanLabel()}
                                            </h3>
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${planInfo.plan_type === "free"
                                                ? "bg-gray-200 text-gray-700"
                                                : planInfo.is_expired
                                                    ? "bg-red-200 text-red-700"
                                                    : "bg-green-200 text-green-700"
                                                }`}>
                                                {planInfo.is_expired ? "Expired" : planInfo.plan_type === "free" ? "Free Tier" : "Premium Active"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Subscription Details */}
                                    <div className="space-y-4 mb-6">
                                        <div className="flex items-center justify-between py-3 border-b border-secondary/20">
                                            <div className="flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-primary" />
                                                <span className="text-sm font-medium text-foreground/80">Expires</span>
                                            </div>
                                            <span className="text-sm font-semibold text-primary">
                                                {planInfo.subscription_end_date
                                                    ? new Date(planInfo.subscription_end_date).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })
                                                    : "Never"}
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between py-3 border-b border-secondary/20">
                                            <div className="flex items-center gap-2">
                                                <Clock className="w-4 h-4 text-primary" />
                                                <span className="text-sm font-medium text-foreground/80">Time Left</span>
                                            </div>
                                            <span className={`text-sm font-semibold ${planInfo.days_remaining && planInfo.days_remaining < 7
                                                ? 'text-red-600'
                                                : planInfo.days_remaining && planInfo.days_remaining < 30
                                                    ? 'text-yellow-600'
                                                    : 'text-green-600'
                                                }`}>
                                                {planInfo.days_remaining !== null ? `${planInfo.days_remaining} days` : "Unlimited"}
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between py-3">
                                            <div className="flex items-center gap-2">
                                                <Shield className="w-4 h-4 text-primary" />
                                                <span className="text-sm font-medium text-foreground/80">Status</span>
                                            </div>
                                            <span className={`text-sm font-semibold ${getPlanStatusColor()}`}>
                                                {planInfo.is_expired ? "Expired" : "Active"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Important Notes */}
                                    {planInfo.subscription_note && planInfo.subscription_note !== "N/A" && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                            <p className="text-xs font-medium text-blue-700 mb-1">Important Note</p>
                                            <p className="text-xs text-blue-600">{planInfo.subscription_note}</p>
                                        </div>
                                    )}

                                    {/* Manage Subscription Button */}
                                    {planInfo.plan_type !== "free" && (
                                        <Button
                                            onClick={handleOpenBillingPortal}
                                            className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
                                        >
                                            <Settings className="w-4 h-4 mr-2" />
                                            Manage Subscription
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    </motion.div>

                    {/* Right Content - Pricing Plans */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="lg:col-span-3"
                    >
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-primary mb-2">Available Plans</h2>
                            <p className="text-foreground/70">Choose the perfect plan for your teaching needs.</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {visiblePlanIndexes.map((idx, index) => {
                                const plan = pricingPlansUpgrade[idx];
                                const priceId = getCorrectPriceId(plan.name);
                                return (
                                    <motion.div
                                        key={plan.name}
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.3 + (index * 0.1) }}
                                        className={`relative bg-white rounded-2xl shadow-lg border-2 p-8 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${plan.highlight
                                            ? "border-primary bg-gradient-to-b from-cream/30 to-white"
                                            : "border-secondary/30 hover:border-primary/30"
                                            }`}
                                    >
                                        {plan.highlight && (
                                            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                                <span className="bg-primary text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                                                    Most Popular
                                                </span>
                                            </div>
                                        )}

                                        <div className="text-center mb-6">
                                            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${plan.highlight ? "bg-primary text-white" : "bg-secondary/20 text-primary"
                                                }`}>
                                                <plan.icon className="w-8 h-8" />
                                            </div>
                                            <h3 className="text-2xl font-bold text-primary mb-2">{plan.name}</h3>
                                            <p className="text-foreground/60 text-sm">{plan.description}</p>
                                        </div>

                                        <div className="text-center mb-8">
                                            <div className="flex items-baseline justify-center mb-2">
                                                <span className="text-4xl font-bold text-primary">{plan.price}</span>
                                            </div>
                                            <span className="text-foreground/60 font-medium">{plan.period}</span>
                                        </div>

                                        <ul className="space-y-3 mb-8">
                                            {plan.features.map((feature, i) => (
                                                <li key={i} className="flex items-start gap-3">
                                                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                                    <span className="text-foreground/80 text-sm">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <Button
                                            onClick={() => handleSubscribe(priceId)}
                                            className={`w-full py-3 font-semibold rounded-xl transition-all duration-200 ${plan.highlight
                                                ? "bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl"
                                                : "bg-secondary hover:bg-secondary/80 text-primary border-2 border-transparent hover:border-primary/20"
                                                }`}
                                        >
                                            {plan.cta}
                                        </Button>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Additional Information */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                            className="mt-8 bg-white rounded-xl border border-secondary/20 p-6"
                        >
                            <h3 className="text-lg font-semibold text-primary mb-3">Subscription Benefits</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-foreground/70">
                                <div className="flex items-center gap-2">
                                    <Check className="w-4 h-4 text-green-500" />
                                    <span>Cancel anytime</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Check className="w-4 h-4 text-green-500" />
                                    <span>Secure payments</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Check className="w-4 h-4 text-green-500" />
                                    <span>24/7 support</span>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            {/* Success Dialog */}
            <Dialog
                open={successDialog.open}
                onClose={() => setSuccessDialog({ open: false, message: "", title: "" })}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minWidth: 400
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                            {successDialog.title}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pt: 0 }}>
                    <Typography variant="body1" color="text.secondary">
                        {successDialog.message}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <MuiButton
                        onClick={() => setSuccessDialog({ open: false, message: "", title: "" })}
                        variant="contained"
                        color="success"
                        sx={{ minWidth: 100, borderRadius: 2 }}
                    >
                        Great!
                    </MuiButton>
                </DialogActions>
            </Dialog>

            {/* Error Dialog */}
            <Dialog
                open={errorDialog.open}
                onClose={() => setErrorDialog({ open: false, message: "", title: "" })}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minWidth: 400
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
                        <Typography variant="h6" fontWeight="bold" color="error.main">
                            {errorDialog.title}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pt: 0 }}>
                    <Typography variant="body1" color="text.secondary">
                        {errorDialog.message}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <MuiButton
                        onClick={() => setErrorDialog({ open: false, message: "", title: "" })}
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: 100, borderRadius: 2 }}
                    >
                        OK
                    </MuiButton>
                </DialogActions>
            </Dialog>

            {/* Info Dialog */}
            <Dialog
                open={infoDialog.open}
                onClose={() => setInfoDialog({ open: false, message: "", title: "" })}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minWidth: 400
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <InfoIcon sx={{ fontSize: 48, color: '#57643F' }} />
                        <Typography variant="h6" fontWeight="bold" sx={{ color: '#57643F' }}>
                            {infoDialog.title}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pt: 0 }}>
                    <Typography variant="body1" color="text.secondary">
                        {infoDialog.message}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <MuiButton
                        onClick={() => setInfoDialog({ open: false, message: "", title: "" })}
                        variant="contained"
                        sx={{
                            minWidth: 100,
                            borderRadius: 2,
                            backgroundColor: '#57643F',
                            '&:hover': {
                                backgroundColor: '#4a5635'
                            }
                        }}
                    >
                        Got it
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Upgrade;